const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://api.trivomtara.com/api"
).replace(/\/$/, "");
const SERVER_ORIGIN = API_BASE_URL.replace(/\/api$/, "");

export function getImageUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  return `${SERVER_ORIGIN}${imagePath}`;
}

export async function apiRequest(url, options = {}, fallbackMessage = "Request failed") {
  let res;
  try {
    res = await fetch(url, options);
  } catch (_err) {
    // fetch only rejects on network-level failures: the server is unreachable,
    // the connection dropped mid-upload, or the response was missing CORS headers.
    throw new Error(
      "Could not reach the server. If you were uploading files, they may be too large — try smaller files or fewer at a time."
    );
  }

  if (res.status === 413) {
    throw new Error("Upload is too large. Please use smaller files and try again.");
  }

  let data;
  try {
    data = await res.json();
  } catch (_err) {
    throw new Error(`${fallbackMessage} (server returned ${res.status})`);
  }

  if (!res.ok || !data.success) {
    throw new Error(data.message || fallbackMessage);
  }
  return data;
}

export function createMasterApi(endpoint) {
  const base = `${API_BASE_URL}${endpoint}`;

  return {
    async list(params = {}) {
      const query = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value != null && value !== "") query.set(key, String(value));
      });
      const qs = query.toString();
      const data = await apiRequest(
        qs ? `${base}?${qs}` : base,
        {},
        "Failed to fetch records"
      );
      return data.data;
    },
    async getOne(id) {
      const data = await apiRequest(`${base}/${id}`, {}, "Failed to fetch record");
      return data.data;
    },
    async create(formDataOrJson, { json = false } = {}) {
      const data = await apiRequest(
        base,
        {
          method: "POST",
          body: formDataOrJson,
          ...(json ? { headers: { "Content-Type": "application/json" } } : {}),
        },
        "Failed to create record"
      );
      return data.data;
    },
    async update(id, formDataOrJson, { json = false } = {}) {
      const data = await apiRequest(
        `${base}/${id}`,
        {
          method: "PUT",
          body: formDataOrJson,
          ...(json ? { headers: { "Content-Type": "application/json" } } : {}),
        },
        "Failed to update record"
      );
      return data.data;
    },
    async remove(id) {
      return apiRequest(`${base}/${id}`, { method: "DELETE" }, "Failed to delete record");
    },
  };
}

export const serviceCategoryApi = createMasterApi("/service-categories");
export const serviceSubCategoryApi = createMasterApi("/service-sub-categories");
export const serviceApi = createMasterApi("/services");
export const specializationApi = createMasterApi("/specializations");
export const userRoleApi = createMasterApi("/user-roles");
export const userLoginApi = createMasterApi("/user-logins");
export const locationTypeApi = createMasterApi("/location-types");
export const locationApi = createMasterApi("/locations");
export const assetTypeApi = createMasterApi("/asset-types");
export const assetSourceLocationApi = createMasterApi("/asset-source-locations");
export const assetInventoryApi = createMasterApi("/asset-inventory");
export const wishlistApi = createMasterApi("/wishlists");
export const cartApi = createMasterApi("/carts");
export const reviewRatingApi = createMasterApi("/review-ratings");
export const userMasterApi = createMasterApi("/users");
export const advertisementApi = createMasterApi("/advertisements");
export const addressTypeApi = createMasterApi("/address-types");
export const addressMasterApi = createMasterApi("/addresses");
export const socialMediaMasterApi = createMasterApi("/social-media-platforms");
export const userSocialMediaProfileApi = createMasterApi("/user-social-media-profiles");
export const userAwardsCertificateApi = createMasterApi("/user-awards-certificates");
export const mediaTypeApi = createMasterApi("/media-types");
export const userNewsMediaApi = createMasterApi("/user-news-media");
export const kycMasterApi = createMasterApi("/kyc-types");
export const userKycApi = createMasterApi("/user-kyc");
export const establishmentKycApi = createMasterApi("/establishment-kyc");
export const bankAccountTypeApi = createMasterApi("/bank-account-types");
export const userBankAccountApi = createMasterApi("/user-bank-accounts");
export const devDarshanApi = createMasterApi("/dev-darshan");
export const orderStatusMasterApi = createMasterApi("/order-statuses");
export const orderMasterApi = createMasterApi("/order-master");
export const shipmentTrackingApi = createMasterApi("/shipment-tracking");
export const callToActionMasterApi = createMasterApi("/call-to-actions");

export async function fetchLinkedAssets(assetId) {
  const res = await fetch(`${API_BASE_URL}/services/${assetId}/linked-assets`);
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || "Failed to fetch linked assets");
  }
  return data.data;
}

export async function syncLinkedAssets(assetId, linkedAssetIds) {
  const res = await fetch(`${API_BASE_URL}/services/${assetId}/linked-assets`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ linked_asset_ids: linkedAssetIds }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || "Failed to update linked assets");
  }
  return data.data;
}
